'use client';

import { Customize, Magnify, Track } from '@/components/_rebrand/Icons';
import ease from '@/constants/ease';
import cx from 'classnames';
import { motion } from 'framer-motion';
import 'keen-slider/keen-slider.min.css';
import Image from 'next/image';
import { FC, useState } from 'react';
import Link from './Link';
export interface AiFeaturesProps {
  backgroundColor: string;
  blurImage: string;
  imageFirst: boolean;
  sectionColorText: string;
  featuresTitleTextColor: string;
  featuresTextColor: string;
  title: React.ReactNode,
  aiFeature: string;
  topFeatures: {
    icon: React.ReactNode,
    title: string;
    description: string;
  }[]
  featuresTitle: string;
  activeFeatureBoxColor: string;
  features: {
    image: React.ReactNode,
    title: string;
    description: string;
  }[]
  comingSoon: boolean;
  companyImage: React.ReactNode,
  companyQuote: string;
  companyQuoteColor: string;
  textColor: string;
  name: string;
  jobTitle: string;
  bannerImage: React.ReactNode;
  bannerImageMobile: React.ReactNode;
  bannerBackgroundColor: string;
  bannerType: string;
  bannerTitle: string;
  bannerLink: React.ReactNode;
}

const AiFeatures: FC<AiFeaturesProps> = ({
  backgroundColor,
  blurImage,
  imageFirst,
  sectionColorText,
  featuresTitleTextColor,
  featuresTextColor,
  aiFeature,
  title,
  topFeatures = [],
  featuresTitle,
  activeFeatureBoxColor,
  features = [],
  comingSoon,
  companyImage,
  companyQuote,
  companyQuoteColor,
  textColor,
  name,
  jobTitle,
  bannerImage,
  bannerImageMobile,
  bannerBackgroundColor,
  bannerType,
  bannerTitle,
  bannerLink,

}) => {
  const [activeFeatureIndex, setFeatureIndex] = useState(0);

  return (
    <div className="bg-violet-900 text-white px-[16px] md:px-[64px] pb-[48px]">
      <div className="flex bg-center">
        <div className="mx-auto max-w-[1384px] overflow-hidden">
          <div style={{ backgroundImage: `url(https://front.com/webassets/rebrand/${blurImage})` }} className={`grid gap-2 md:gap-3 text-left ${backgroundColor} ${sectionColorText} rounded-[16px] bg-center bg-top bg-no-repeat`}>
            <div className="flex justify-left md:justify-center flex-wrap pt-[64px] pb-[48px] md:pb-[120px] px-[16px] md:px-0 overflow-hidden">
              <div className="flex w-full md:justify-center">
                <div className="flex bg-violet-1000 rounded-[16px] md:ml-[32px] items-center">
                  <div className="flex" id="icon">
                    <span className="flex w-[32px] h-[32px] bg-citron-500 rounded-[50%] justify-center items-center mr-[8px] mt-[2px] mb-[2px] ml-[2px]">
                      <Image src="https://front.com/webassets/rebrand/ai-icon.svg" width={37} height={37} alt="ai icon" />
                    </span>
                  </div>
                  <div className="flex text-p2 text-white font-500 mr-[13px]" id="textHeader">
                    {aiFeature}
                  </div>
                </div>
              </div>
              <div className="flex md:justify-center">
                <h2 className={`text-h3 md:text-h1 text-left md:text-center mt-[32px] mb-[64px] ${featuresTitleTextColor}`}>
                  {title}
                </h2>
              </div>
              <div className="flex w-full justify-center flex-wrap md:flex-nowrap">
                {topFeatures.map((feature, index) => (
                  <div key={`top-features-${index}`} className="max-w-[100%] md:max-w-[356px] w-full mr-0 md:mr-[75px] pb-[32px] md:p-0 md:px-0 md:first:ml-[64px]">
                    <div className="flex bg-citron-500 rounded-[50%] w-[32px] h-[32px] justify-center items-center">
                      {feature.icon}
                    </div>
                    <div className={`flex font-500 text-p1 mt-[12px] mb-[16px] ${featuresTitleTextColor}`}>
                      {feature.title}
                    </div>
                    <div className={`flex text-p2 ${featuresTitleTextColor}`}>
                      {feature.description}
                    </div>
                  </div>

                ))}
              </div>

              <div className={`flex w-full flex-wrap md:flex-nowrap justify-center items-start mt-0 md:mt-[112px] ${imageFirst ? "flex-row-reverse" : null}`}>
                <div className={`flex flex-wrap w-full overflow-hidden md:w-[50%] ${imageFirst ? "mr-0 ml-0 px-0 md:mr-[32px] md:ml-[140px]" : "mr-0 ml-0  md:ml-[32px] md:mr-[140px]"}`}>
                  <div className={`${featuresTitleTextColor} text-h1 font-500 mb-[48px] md:ml-[32px]`}>{featuresTitle}</div>
                  <div className="hidden sm:flex flex-wrap">
                    {features.map((feature, index) => (
                      <div key={`ai-feature-${index}`} className={`flex w-full flex-wrap cursor-pointer mt-[24px] ${activeFeatureIndex === index ? `${activeFeatureBoxColor} rounded-[16px]` : null}`} onClick={() => setFeatureIndex(index)}>
                        <div className={`flex ml-[16px] md:ml-[32px] font-500 text-p1 pt-[24px] ${featuresTitleTextColor}`}>
                          {feature.title}
                        </div>
                        {activeFeatureIndex === index && (
                          <motion.div
                            className={`flex ${featuresTextColor} text-p2 pl-[32px] pr-[32px] pb-[32px] mt-[24px]`}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              duration: 400 / 1000,
                              ease: ease.appear,
                            }}
                          >
                            {feature.description}
                          </motion.div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex sm:hidden flex-wrap">
                    {features.map((feature, index) => (
                      <div key={`ai-feature-${index}`} className={`flex w-full flex-wrap cursor-pointer mt-[24px] ${activeFeatureBoxColor} rounded-[16px]`}>
                        <div className="flex ml-[16px] md:ml-[32px] font-500 text-p1 pt-[24px]">
                          {feature.title}
                        </div>

                        <motion.div
                          className={`flex ${sectionColorText} text-p2 pl-[32px] pr-[32px] pb-[32px] mt-[24px]`}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 400 / 1000,
                            ease: ease.appear,
                          }}
                        >
                          {feature.description}
                        </motion.div>

                      </div>
                    ))}
                  </div>
                </div>
                <div className={`hidden sm:flex w-full md:w-[50%] justify-center mt-[18px] md:mt-0 ${imageFirst ? " ml-0 md:ml-[64px]" : "mr-0 md:mr-[64px]"}`}>
                  {features[activeFeatureIndex].image}
                </div>
              </div>
              {comingSoon && (
                <div id="smartqa" className="flex flex-wrap items-center w-full bg-violet-300 rounded-[16px] md:ml-[24px] md:mr-[24px] mt-[48px] md:mt-[112px] p-[16px] md:p-0">
                  <div className="flex flex-wrap md:flex-nowrap items-center md:mt-[80px]">
                    <div className="flex flex-wrap w-full md:w-[50%] pl-0 md:pl-[42px]">
                      <h2 className="text-blue-900 text-h1 font-500 mb-[16px]">Smart QA: Get a complete view of agent performance</h2>
                      <div className="text-blue-900 text-p2">Replace manual ticket-by-ticket reviews with AI-powered scorecards and comprehensive reporting. Accelerate your QA process and receive a more consistent, objective view of support quality with Smart QA.</div>
                      <div className="flex mt-[32px]">
                        <Link
                          className={cx('block button button-size-medium button-primary-citron')}
                          href="https://front.com/request-demo?src=smartQA"
                        >
                          Request a demo
                        </Link>
                      </div>
                    </div>
                    <div className="flex w-full md:w-[50%] pl-0 md:pl-[64px]">
                      <div className="flex rounded-[16px] bg-violet-400 md:mr-[40px] mt-[32px] md:mt-0">
                        <Image src="https://front.com/assets/pages/product/ai/smart-qa-capabilities.png" width={612} height={465} alt="automated deflection" />
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full flex-wrap md:flex-nowrap justify-center mt-[64px] md:mb-[110px] mx-[0] md:mx-[42px]">
                    <div className="max-w-[100%] md:max-w-[356px] mr-0 md:mr-[75px] md:px-0 mb-[32px] md:mb-0">
                      <div className="flex bg-citron-500 rounded-[50%] w-[32px] h-[32px] justify-center items-center">
                        {Customize()}
                      </div>
                      <div className="flex text-blue-900 font-500 text-p1 mt-[12px] mb-[16px]">
                        Customize your scorecard
                      </div>
                      <div className="flex text-blue-900 text-p2">
                        Select the criteria that matter most from our extensive library like tone, professionalism, and personalization.
                      </div>
                    </div>
                    <div className="max-w-[100%] md:max-w-[356px] mr-0 md:mr-[75px] md:px-0 mb-[32px] md:mb-0">
                      <div className="flex bg-citron-500 rounded-[50%] w-[32px] h-[32px] justify-center items-center">
                        {Magnify()}
                      </div>
                      <div className="flex text-blue-900 font-500 text-p1 mt-[12px] mb-[16px]">
                        Auto-review every ticket
                      </div>
                      <div className="flex text-blue-900 text-p2">
                        Get an automated QA scorecard on each closed ticket for a more accurate view of agent skills and performance.
                      </div>
                    </div>
                    <div className="max-w-[100%] md:max-w-[356px] md:px-0 mb-[32px] md:mb-0">
                      <div className="flex bg-citron-500 rounded-[50%] w-[32px] h-[32px] justify-center items-center">
                        {Track()}
                      </div>
                      <div className="flex text-blue-900 font-500 text-p1 mt-[12px] mb-[16px]">
                        Track quality at scale
                      </div>
                      <div className="flex text-blue-900 text-p2">
                        Understand team performance and support effectiveness with dedicated analytics for your QA metrics.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={`flex ${sectionColorText} justify-center flex-wrap pt-[64px] md:pt-[110px]`}>
                {companyImage}
                <div className={`flex text-p2 md:text-h2 font-500 justify-center text-center font-serif md:px-[160px] mt-[30px] mb-[25px] ${companyQuoteColor}`}>
                  {companyQuote}
                </div>
                <div className={`flex w-full flex-wrap md:flex-nowrap justify-center items-center ${textColor} font-500 text-p2 mb-[64px] md:mb-[114px]`}>
                  {name} <span className="uppercase text-center sm:text-left text-micro1 pl-[14px]">&nbsp;{jobTitle}</span>
                </div>
                <div className={`flex flex-wrap md:flex-nowrap items-center ${bannerBackgroundColor} ${sectionColorText} rounded-[16px] max-w-[1036px] mx-[16px]`}>
                  <div className="flex w-full md:w-[20%] hidden md:block">
                    {bannerImage}
                  </div>
                  <div className="flex w-full md:w-[20%] block md:hidden">
                    {bannerImageMobile}
                  </div>
                  <div className="flex flex-wrap ml-0 md:ml-[34px] px-[16px] md:px-0 w-full md:w-[60%]">
                    <div className="flex w-full uppercase text-micro1 mt-[24px] md:mt-0">
                      {bannerType}
                    </div>
                    <div className={`flex text-h3 font-500 ${featuresTitleTextColor}`}>
                      {bannerTitle}
                    </div>
                  </div>
                  <div className="flex w-full md:w-[20%] px-[16px] md:px-0 my-[24px] md:my-0">
                    {bannerLink}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AiFeatures;
